import { forwardRef, useImperativeHandle, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DEPENDENT_RELATIONSHIP_OPTIONS } from "@shared/constants/dependentRelationships";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatSSNInput } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface DependentQuickAddFormProps {
  /** Called after a dependent is created successfully. Receives the created record. */
  onSaved?: (created: any) => void;
  /** Pre-select the relationship (e.g. "spouse" for marriage, "child" for birth). */
  defaultRelationship?: string;
  submitLabel?: string;
  /** When provided, renders a Cancel button. */
  onCancel?: () => void;
  /** Hide the Add/Cancel buttons — parent drives creation via the ref's flushDraft(). */
  hideButtons?: boolean;
}

export interface DependentQuickAddHandle {
  /**
   * Create the dependent currently in the fields, if any.
   * - empty form  → resolves null (nothing to do)
   * - complete    → creates it, resolves the created record (and clears fields)
   * - partial     → toasts a warning and throws (caller should abort)
   */
  flushDraft: () => Promise<any | null>;
}

const emptyDependent = (relationship = "") => ({
  firstName: "",
  lastName: "",
  relationship,
  dateOfBirth: "",
  ssn: "",
  sex: "",
  isStudent: false,
});

/**
 * Inline (no Dialog wrapper) add-only dependent form. Creates immediately via
 * /api/employees/me/dependents. Shared by Profile's modal and the QLE flow.
 */
export const DependentQuickAddForm = forwardRef<
  DependentQuickAddHandle,
  DependentQuickAddFormProps
>(function DependentQuickAddForm(
  {
    onSaved,
    defaultRelationship = "",
    submitLabel = "Add dependent",
    onCancel,
    hideButtons = false,
  }: DependentQuickAddFormProps,
  ref
) {
  const { toast } = useToast();
  const [dependent, setDependent] = useState(() =>
    emptyDependent(defaultRelationship)
  );

  const addMutation = useMutation({
    mutationFn: async (data: any) => {
      try {
        const response = await apiRequest(
          "POST",
          "/api/employees/me/dependents",
          data
        );
        return response.json();
      } catch {
        const response = await apiRequest(
          "POST",
          "/api/employees/dependents",
          data
        ); // fallback
        return response.json();
      }
    },
    onSuccess: (created: any) => {
      toast({
        title: "Dependent Added",
        description: `${dependent.firstName} ${dependent.lastName} has been added successfully.`,
        variant: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/employees/me/dependents"],
      });
      setDependent(emptyDependent(defaultRelationship));
      onSaved?.(created);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to add dependent. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (
      !dependent.firstName ||
      !dependent.lastName ||
      !dependent.relationship ||
      !dependent.dateOfBirth
    ) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }
    addMutation.mutate(dependent);
  };

  useImperativeHandle(ref, () => ({
    flushDraft: async () => {
      // Nothing typed (relationship may be pre-filled) → nothing to create.
      const hasAnyInput =
        dependent.firstName ||
        dependent.lastName ||
        dependent.dateOfBirth ||
        dependent.ssn ||
        dependent.sex;
      if (!hasAnyInput) return null;

      if (
        !dependent.firstName ||
        !dependent.lastName ||
        !dependent.relationship ||
        !dependent.dateOfBirth
      ) {
        toast({
          title: "Incomplete dependent",
          description:
            "Finish the dependent's required fields, or clear them, before submitting.",
          variant: "destructive",
        });
        throw new Error("incomplete-dependent");
      }
      return addMutation.mutateAsync(dependent);
    },
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="depFirstName">First Name *</Label>
          <Input
            id="depFirstName"
            value={dependent.firstName}
            onChange={(e) =>
              setDependent((prev) => ({ ...prev, firstName: e.target.value }))
            }
            placeholder="Enter first name"
          />
        </div>
        <div>
          <Label htmlFor="depLastName">Last Name *</Label>
          <Input
            id="depLastName"
            value={dependent.lastName}
            onChange={(e) =>
              setDependent((prev) => ({ ...prev, lastName: e.target.value }))
            }
            placeholder="Enter last name"
          />
        </div>
        <div>
          <Label htmlFor="depRelationship">Relationship *</Label>
          <Select
            value={dependent.relationship}
            onValueChange={(value) =>
              setDependent((prev) => ({ ...prev, relationship: value }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select relationship" />
            </SelectTrigger>
            <SelectContent>
              {DEPENDENT_RELATIONSHIP_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="depDateOfBirth">Date of Birth *</Label>
          <Input
            id="depDateOfBirth"
            type="date"
            value={dependent.dateOfBirth}
            onChange={(e) =>
              setDependent((prev) => ({ ...prev, dateOfBirth: e.target.value }))
            }
          />
        </div>
        <div>
          <Label htmlFor="depSsn">Social Security Number</Label>
          <Input
            id="depSsn"
            value={dependent.ssn}
            onChange={(e) =>
              setDependent((prev) => ({
                ...prev,
                ssn: formatSSNInput(e.target.value),
              }))
            }
            placeholder="XXX-XX-XXXX"
            maxLength={11}
          />
        </div>
        <div>
          <Label htmlFor="depSex">Sex</Label>
          <Select
            value={dependent.sex}
            onValueChange={(value) =>
              setDependent((prev) => ({ ...prev, sex: value }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select sex" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {!hideButtons && (
        <div className="flex justify-end space-x-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={addMutation.isPending}
          >
            {addMutation.isPending ? "Adding..." : submitLabel}
          </Button>
        </div>
      )}
    </div>
  );
});
